import { useEffect, useMemo } from "react";
import type { Circle } from "@core/types/core.types";
import type { ChatAvailableCircle, TalkJSData } from "../chat.types";

/**
 * The dashboard chat surface needs to render exactly one conversation,
 * but a user can be in multiple circles. This hook owns the resolution
 * across the four cases the surface has to handle:
 *
 *  - empty: the user has no chat-eligible circles in the active program
 *    (or chat is gated upstream and the payload is empty). Caller
 *    renders the "no conversations" empty state.
 *  - auto: exactly one chat-eligible circle. No picker, no store write —
 *    the conversation just renders.
 *  - selected: multiple circles, the user has picked one and that pick
 *    is still valid. Render that conversation; show a small switcher
 *    affordance so they can change their mind.
 *  - needs-picker: multiple circles, no valid pick recorded. Render
 *    the expanded in-sheet picker; the user's choice flows through
 *    `setSelectedCircle` and the next render lands on `selected`.
 *
 * Stale-selection handling: if the persisted selection points at a
 * circle no longer in the user's chat-eligible set (admin removed
 * them, circle deleted, etc.), the resolver returns `needs-picker`
 * and clears the stale entry as a side effect — never synchronously
 * during render. The cleanup re-renders the consumer with a clean
 * store, which is fine because the second render already returned
 * `needs-picker` and renders the same UI.
 *
 * The hook is store-agnostic: callers thread the persisted selection
 * state and clearer action in. The platform layer wires this from
 * `useChatSelection()`; tests can pass plain values.
 */
export type ResolvedChatCircle =
  | {
      kind: "empty";
    }
  | {
      kind: "auto";
      circleId: number;
      conversationId: string;
      availableCircles: ChatAvailableCircle[];
    }
  | {
      kind: "selected";
      circleId: number;
      conversationId: string;
      availableCircles: ChatAvailableCircle[];
    }
  | {
      kind: "needs-picker";
      availableCircles: ChatAvailableCircle[];
    };

export type UseResolvedChatCircleArgs = {
  /** The user's circles for the active program (full objects with name + program_id). */
  userCircles: Circle[] | null | undefined;
  /** Active program whose chat surface this resolution drives. */
  programId: number | null;
  /** TalkJS init payload — the source of truth for which circles have provisioned conversations. */
  talkjsData: TalkJSData | null | undefined;
  /** Persisted per-program selection (Record<programId, circleId>). */
  selectedCircleByProgram: Record<number, number>;
  /** Clears the selection for a program (used to evict stale entries). */
  clearSelectedCircle: (programId: number) => void;
};

export function useResolvedChatCircle({
  userCircles,
  programId,
  talkjsData,
  selectedCircleByProgram,
  clearSelectedCircle,
}: UseResolvedChatCircleArgs): ResolvedChatCircle {
  // Build availableCircles by intersecting the payload's
  // {circle_id, talkjs_conversation_id} entries with the user's
  // circles list (which carries the display name). Memoised so
  // downstream consumers don't see a new array identity on every
  // render of the parent.
  const availableCircles = useMemo<ChatAvailableCircle[]>(() => {
    if (!talkjsData || !userCircles || programId == null) return [];

    const userCirclesById = new Map<number, Circle>();
    for (const circle of userCircles) {
      if (circle.program_id === programId) {
        userCirclesById.set(circle.id, circle);
      }
    }

    const result: ChatAvailableCircle[] = [];
    for (const entry of talkjsData.conversations) {
      const circle = userCirclesById.get(entry.circle_id);
      if (!circle) continue;
      result.push({
        circle_id: entry.circle_id,
        name: circle.name,
        conversation_id: entry.talkjs_conversation_id,
      });
    }
    return result;
  }, [talkjsData, userCircles, programId]);

  const persistedCircleId =
    programId != null ? selectedCircleByProgram[programId] : undefined;

  const persistedIsValid =
    persistedCircleId != null &&
    availableCircles.some((c) => c.circle_id === persistedCircleId);

  // Evict a stale persisted selection. Done in an effect (not during
  // render) so we never call setState while rendering. The next render
  // sees the cleaned store and returns `needs-picker` straightforwardly.
  useEffect(() => {
    if (
      programId != null &&
      persistedCircleId != null &&
      availableCircles.length > 0 &&
      !persistedIsValid
    ) {
      clearSelectedCircle(programId);
    }
  }, [
    programId,
    persistedCircleId,
    persistedIsValid,
    availableCircles,
    clearSelectedCircle,
  ]);

  if (availableCircles.length === 0) {
    return { kind: "empty" };
  }

  if (availableCircles.length === 1) {
    const only = availableCircles[0];
    return {
      kind: "auto",
      circleId: only.circle_id,
      conversationId: only.conversation_id,
      availableCircles,
    };
  }

  if (persistedIsValid) {
    const selected = availableCircles.find(
      (c) => c.circle_id === persistedCircleId
    )!;
    return {
      kind: "selected",
      circleId: selected.circle_id,
      conversationId: selected.conversation_id,
      availableCircles,
    };
  }

  return { kind: "needs-picker", availableCircles };
}

export default useResolvedChatCircle;
